import {
    atom,
    useRecoilValue,
    useSetRecoilState,
} from "recoil";
import { urlSyncEffect } from "recoil-sync";
import { array, number } from "@recoiljs/refine";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { axios } from "../lib/request";
import { designationsUri, designationsQueryKey } from "../lib/refresh";
import { useItemQuery } from "../Global.state";
import { INFINITE_QUERY_KEY, MAX_PER_PAGE } from "../consts/request";
import { useEffect } from "react";
import { OPEN_STATES_KEYS } from "../consts/states";
import { getErrorMessage } from "../lib/error";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";

export const deleteIdState = atom({
    key: "designation-items/deleteId",
    default: 0,
});

export const pageState = atom({
    key: "designation-items/page",
    default: 1,
    effects: [
        urlSyncEffect({
            refine: number(),
            history: 'push'
        })
    ],
});

export const pageSizeState = atom({
    key: "designation-items/pageSize",
    default: MAX_PER_PAGE,
    effects: [
        urlSyncEffect({
            refine: number(),
            history: 'push'
        })
    ],
});

export const highlightIdState = atom({
    key: "designation-items/highlightId",
    default: 0,
})

export const termState = atom({
    key: "designation-items/term",
    default: "",
});

export const openTabState = atom({
    key: OPEN_STATES_KEYS.DESIGNATION,
    default: [],
    effects: [
        urlSyncEffect({
            refine: array(number()),
            history: 'push'
        })
    ],
});

export const fetchDesignations = async (game_id, term, page, per_page) => {
    if (game_id === "new") {
        return { designations: [] };
    }

    const params = { term, page, per_page };
    const { data } = await axios.get(designationsUri(game_id), { params });
    data.designations = (data.designations || []).map(designation => {
        if (designation?.media_url) {
            // Transform NPC media into standard structure
            designation.assets = {
                photos: [{
                    name: 'media',
                    url: designation.media_url,
                }]
            }
        }
        return designation
    })
    return data
};

export const updateDesignation = async (game_id, id, params) => {
    const url = `${process.env.REACT_APP_API_DESIGNATION_UPDATE_URL}/${id}.json`;
    return await axios.put(url, { ...params, game_id });
};

export const useQueryKey = (game_id) => {
    const page = useRecoilValue(pageState);
    const pageSize = useRecoilValue(pageSizeState);
    const term = useRecoilValue(termState);
    return designationsQueryKey(game_id, term, page, pageSize);
};

export const useInfiniteQueryKey = (game_id) => {
    return designationsQueryKey(game_id, INFINITE_QUERY_KEY, 1, MAX_PER_PAGE)
}

export const useDesignationsDataQuery = (game_id) => {
    const term = useRecoilValue(termState);
    const page = useRecoilValue(pageState);
    const pageSize = useRecoilValue(pageSizeState);
    const queryKey = useQueryKey(game_id);
    const setAlert = useSetRecoilState(alertState);
    return useQuery(
        queryKey,
        () => fetchDesignations(game_id, term, page, pageSize),
        { 
            keepPreviousData: true, suspense: true,
            useErrorBoundary: false,
            onError: (error) => {
                const message = getErrorMessage(error);
                setAlert({ type: "error", message, closable: true });
            }
        }
    );
};

export const useAllDesignationsQuery = () => {
    const { game_id } = useParams();

    const {
        data,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetched,
    } = useInfiniteQuery(
        useInfiniteQueryKey(game_id),
        ({ pageParam }) => fetchDesignations(game_id, "", pageParam, MAX_PER_PAGE),
        {
            getNextPageParam: (lastPage, pages) => {
                const { count } = lastPage?.meta || {};
                const loaded = pages.reduce(
                    (sum, page) => (sum += page?.designations.length),
                    0
                );

                if ((lastPage && (lastPage?.designations || []).length === 0) || count <= loaded) {
                    return undefined;
                }

                return Number(lastPage?.meta?.page || 0) + 1;
            },
        }
    );

    const designations = (data?.pages || []).reduce((result, page) => {
        return [
            ...result,
            ...(page?.designations || []),
        ]
    }, [])

    const nextPage = [...data?.pageParams || []].pop()

    return {
        nextPage,
        designations,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetched,
    };
};

export const useInfiniteDesignations = () => {
    const {
        nextPage,
        designations,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetched,
    } = useAllDesignationsQuery()

    useEffect(() => {
        let loaded = false
        if (!loaded && hasNextPage) {
            fetchNextPage()
        }
        return () => {
            loaded = true
        }
    }, [
        nextPage,
        hasNextPage,
        fetchNextPage,
    ])
    
    return {
        isLoading: isFetchingNextPage,
        isFetched,
        designations,
    }
}

export const useDesignationsQuery = (game_id) => {
    const { data } = useDesignationsDataQuery(game_id);
    return data?.designations;
};

export const useDesignationItemsCountQuery = (game_id) => {
    const { data } = useDesignationsDataQuery(game_id);
    return data?.meta?.count;
};

export const useDesignationItemsFetchingQuery = (game_id) => {
    const { isFetching } = useDesignationsDataQuery(game_id);
    return isFetching;
};

export const useDesignationItemsPagesQuery = (game_id) => {
    const { data } = useDesignationsDataQuery(game_id);
    return data?.meta?.pages;
};

export const useDesignationItemsQuery = (id, config) => {
    const { game_id } = useParams();
    const { data, isFetching } = useDesignationsDataQuery(game_id);
    const queryKey = useQueryKey(game_id);
    const setter = (params) => updateDesignation(game_id, id, params);

    return [...useItemQuery(data, id, "designations", queryKey, setter, config), isFetching];
};

export const useQueryNoInvalidate = (id) => useDesignationItemsQuery(id, { invalidate: false });
export const useQueryInvalidateInfinite = (id) => {
    const { game_id } = useParams();
    const page = useRecoilValue(pageState);
    return useDesignationItemsQuery(id, {
        invalidateKey: useInfiniteQueryKey(game_id),
        refetchType: 'none',
        refetchPage: data => {
            return String(data?.meta?.page) === String(page)
        },
    })
}

export const useCreateMutation = () => {
    const { game_id } = useParams();
    const queryClient = useQueryClient();
    const pageSize = useRecoilValue(pageSizeState);
    const setPage = useSetRecoilState(pageState);
    const setTerm = useSetRecoilState(termState);
    const queryKey = useQueryKey(game_id);

    const { isLoading, mutateAsync } = useMutation(
        () =>
            axios.post(process.env.REACT_APP_API_DESIGNATION_URL, {
                game_id,
            }),
        {
            onSuccess: (data) => {
                const current = queryClient.getQueryData(queryKey);
                const newDesignations = [data.data, ...current.designations]
                const newPages = Math.ceil(
                    newDesignations.length / pageSize
                )
                queryClient.setQueryData(queryKey, {
                    ...current,
                    designations: newDesignations,
                    meta: {
                        ...current?.meta || {},
                        pages: newPages,
                    },
                });
                setPage(1);
                setTerm("");
                queryClient.invalidateQueries(queryKey)
            },
        }
    );

    return {
        isCreating: isLoading,
        create: mutateAsync,
    };
};

export const useDestroyMutation = () => {
    const { game_id } = useParams();
    const pageSize = useRecoilValue(pageSizeState);
    const queryClient = useQueryClient();
    const queryKey = useQueryKey(game_id);

    const { isLoading, mutate } = useMutation(
        (deleteId) =>
            axios.delete(
                `${process.env.REACT_APP_API_DESIGNATION_DELETE_URL}/${deleteId}.json?game_id=${game_id}`
            ),
        {
            onMutate: (deleteId) => {
                const data = queryClient.getQueryData(queryKey);
                const designations = data.designations.filter((designation) => designation.id !== deleteId);
                const existingPages = (data?.meta?.pages || 1)
                const newPages = Math.ceil(
                    designations.length / pageSize
                )
                queryClient.setQueryData(queryKey, {
                    ...data,
                    designations,
                    meta: {
                        ...data?.meta || {},
                        pages: Math.max(
                            newPages,
                            existingPages - (designations.length > 0 ? 0 : 1)
                        ),
                    },
                });
                queryClient.cancelQueries(queryKey);
            },
        }
    );

    return {
        isDeleting: isLoading,
        destroy: mutate,
    };
};

export const useInvalidateInfinity = () => {
    const { game_id } = useParams();
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries(designationsQueryKey(game_id, INFINITE_QUERY_KEY, 1, MAX_PER_PAGE))
}